import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFilters } from '../contexts/FilterContext';
import CategoryTreePanel from '../components/shared/CategoryTreePanel';
import { API } from '../utils/api';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';

// Import refactored components
import { 
  StatsGrid, 
  CountryPieChart, 
  USStatesBarChart, 
  DocumentTypesChart, 
  TopWordsChart,
  PollStatsCard,
  XPEasterEggAnalyticsSuite
} from '../components/Statistics';
import { TopSellersLeaderboard, MostCopiedLeaderboard } from '../components/Statistics';
import { ExtensiveAnalyticsDashboard } from '../components/Analytics';

// Import Book Advertisement
import { MediumBookAd } from '../components/Ads/BookAdvertisements';
import UltraDenseControlPanel from '../components/UltimateSearch/UltraDenseControlPanel';
import PowerUserControlPanel from '../components/common/PowerUserControlPanel';

// Fix for leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Constants
const FUNNY_STATS_MESSAGES = [
  "📊 These statistics are so accurate, even your math teacher would be impressed!",
  "🎯 Data so hot, it's practically on fire! 🔥",
  "💡 Warning: Viewing these stats may cause sudden urges to buy protocols!",
  "🚀 Our numbers go up like Richard J. Selman's A-4 Skyhawk - FAST!",
  "📈 These charts are more exciting than a supernatural thriller! (Speaking of which...)",
];

const COLORS = ['#FDE047', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#FDE68A', '#14b8a6', '#F59E0B'];

// Coordinate maps
const STATE_COORDINATES = {
  'Alabama': [32.806671, -86.791130], 'Alaska': [61.370716, -152.404419],
  'Arizona': [33.729759, -111.431221], 'Arkansas': [34.969704, -92.373123],
  'California': [36.116203, -119.681564], 'Colorado': [39.059811, -105.311104],
  'Connecticut': [41.597782, -72.755371], 'Delaware': [39.318523, -75.507141],
  'Florida': [27.766279, -81.686783], 'Georgia': [33.040619, -83.643074],
  'Hawaii': [21.094318, -157.498337], 'Idaho': [44.240459, -114.478828],
  'Illinois': [40.349457, -88.986137], 'Indiana': [39.849426, -86.258278],
  'Iowa': [42.011539, -93.210526], 'Kansas': [38.526600, -96.726486],
  'Kentucky': [37.668140, -84.670067], 'Louisiana': [31.169546, -91.867805],
  'Maine': [44.693947, -69.381927], 'Maryland': [39.063946, -76.802101],
  'Massachusetts': [42.230171, -71.530106], 'Michigan': [43.326618, -84.536095],
  'Minnesota': [45.694454, -93.900192], 'Mississippi': [32.741646, -89.678696],
  'Missouri': [38.456085, -92.288368], 'Montana': [46.921925, -110.454353],
  'Nebraska': [41.125370, -98.268082], 'Nevada': [38.313515, -117.055374],
  'New Hampshire': [43.452492, -71.563896], 'New Jersey': [40.298904, -74.521011],
  'New Mexico': [34.840515, -106.248482], 'New York': [42.165726, -74.948051],
  'North Carolina': [35.630066, -79.806419], 'North Dakota': [47.528912, -99.784012],
  'Ohio': [40.388783, -82.764915], 'Oklahoma': [35.565342, -96.928917],
  'Oregon': [44.572021, -122.070938], 'Pennsylvania': [40.590752, -77.209755],
  'Rhode Island': [41.680893, -71.511780], 'South Carolina': [33.856892, -80.945007],
  'South Dakota': [44.299782, -99.438828], 'Tennessee': [35.747845, -86.692345],
  'Texas': [31.054487, -97.563461], 'Utah': [40.150032, -111.862434],
  'Vermont': [44.045876, -72.710686], 'Virginia': [37.769337, -78.169968],
  'Washington': [47.400902, -121.490494], 'West Virginia': [38.491226, -80.954456],
  'Wisconsin': [44.268543, -89.616508], 'Wyoming': [42.755966, -107.302490]
};

const COUNTRY_COORDINATES = {
  'United States': [39.8283, -98.5795], 'Canada': [56.1304, -106.3468],
  'United Kingdom': [55.3781, -3.4360], 'Germany': [51.1657, 10.4515],
  'France': [46.2276, 2.2137], 'Japan': [36.2048, 138.2529],
  'Australia': [-25.2744, 133.7751], 'Brazil': [-14.2350, -51.9253],
  'India': [20.5937, 78.9629], 'China': [35.8617, 104.1954]
};

// Sub-components
const HeroBanner = ({ funnyMessage, funnyFact }) => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(253, 224, 71, 0.3) 0%, rgba(253, 230, 138, 0.2) 100%)',
    borderRadius: 20, padding: 30, marginBottom: 30,
    border: '1px solid rgba(253, 224, 71, 0.3)'
  }}>
    <h1 style={{ 
      fontSize: '2.5rem', fontWeight: 800, marginBottom: 10,
      background: 'linear-gradient(135deg, #047857 0%, #0F172A 100%)',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
    }}>
      📈 InfoPilot Statistics Central
    </h1>
    <p style={{ color: '#047857', fontSize: '0.75rem', letterSpacing: 1.2, marginBottom: 8, fontWeight: 500 }}>Advanced Research Platform &middot; Information Exchange Network</p>
    <p style={{ color: '#a1a1aa', fontSize: '1.1rem', marginBottom: 15 }}>{funnyMessage}</p>
    {funnyFact && (
      <div style={{ 
        display: 'inline-block', background: 'rgba(16, 185, 129, 0.2)',
        padding: '8px 16px', borderRadius: 20, color: '#10b981', fontSize: '0.9rem'
      }}>
        🎭 {funnyFact}
      </div>
    )}
  </div>
);

const QuickStatsGrid = ({ stats }) => {
  const quickStats = [
    { label: 'Total Users', value: stats?.overview?.total_users || 0, icon: '👥', color: '#FDE047' },
    { label: 'Active (7d)', value: stats?.overview?.active_users_7d || 0, icon: '🔥', color: '#ef4444' },
    { label: 'Protocols', value: stats?.overview?.total_protocols || 0, icon: '📋', color: '#3b82f6' },
    { label: 'Searches', value: stats?.overview?.total_searches || 0, icon: '🔍', color: '#10b981' },
    { label: 'Purchases', value: stats?.overview?.total_purchases || 0, icon: '💰', color: '#f59e0b' },
    { label: 'Revenue', value: `$${stats?.overview?.total_revenue || 0}`, icon: '💵', color: '#047857' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, marginBottom: 30 }}>
      {quickStats.map((stat, i) => (
        <div key={i} style={{
          background: '#334155', borderRadius: 16, padding: 20,
          border: `1px solid ${stat.color}30`, textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: 10 }}>{stat.icon}</div>
          <div style={{ color: stat.color, fontSize: '2rem', fontWeight: 700 }}>{stat.value}</div>
          <div style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
};

const InteractiveStatsMap = ({ mapMarkers, selectedStatType, onClearMap, onSelectStat, stats, leaderboard, mostCopied }) => {
  const createStatMarker = (color, size = 20) => L.divIcon({
    className: 'custom-stat-marker',
    html: `<div style="background:${color};width:${size}px;height:${size}px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
    iconSize: [size, size], iconAnchor: [size/2, size/2], popupAnchor: [0, -size/2],
  });

  return (
    <div style={{
      background: '#334155', borderRadius: 16, padding: 25,
      marginBottom: 30, border: '1px solid rgba(253, 224, 71, 0.2)'
    }} data-testid="interactive-stats-map">
      <h3 style={{ color: '#0F172A', marginBottom: 15 }}>🗺️ Interactive Statistics Map</h3>
      <div style={{ display: 'flex', gap: 10, marginBottom: 15, flexWrap: 'wrap' }}>
        {[
          { type: 'countries', label: '🌍 Countries', data: stats?.countries?.countries },
          { type: 'us_states', label: '🇺🇸 US States', data: stats?.us_states?.states },
          { type: 'top_sellers', label: '🏆 Top Sellers', data: leaderboard?.sales },
          { type: 'most_copied', label: '📋 Most Copied', data: mostCopied?.leaderboard },
        ].map(btn => (
          <button
            key={btn.type}
            onClick={() => onSelectStat(btn.type, btn.data)}
            className={`btn ${selectedStatType === btn.type ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
          >
            {btn.label}
          </button>
        ))}
        {mapMarkers.length > 0 && (
          <button onClick={onClearMap} className="btn btn-secondary" style={{ marginLeft: 'auto', padding: '8px 16px' }}>
            Clear Map
          </button>
        )}
      </div>
      
      <div style={{ height: 400, borderRadius: 12, overflow: 'hidden' }}>
        <MapContainer center={[39.8283, -98.5795]} zoom={4} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          />
          {mapMarkers.map((marker, idx) => (
            <React.Fragment key={marker.id}>
              <Marker position={marker.position} icon={createStatMarker(marker.color, 24)}>
                <Popup>
                  <div style={{ textAlign: 'center' }}>
                    <strong style={{ color: marker.color }}>{marker.label}</strong>
                    <br />
                    <span>{marker.type === 'seller' ? 'Sales' : marker.type === 'protocol' ? 'Copies' : 'Count'}: {marker.value}</span>
                  </div>
                </Popup>
              </Marker>
              <Circle center={marker.position} radius={marker.value * 1000} pathOptions={{ color: marker.color, fillColor: marker.color, fillOpacity: 0.2 }} />
            </React.Fragment>
          ))}
        </MapContainer>
      </div>
      
      {mapMarkers.length === 0 && (
        <p style={{ textAlign: 'center', padding: 20, color: '#a1a1aa' }}>👆 Click a button above to populate the map!</p>
      )}
    </div>
  );
};

const PollStatsSection = ({ userPollStats, pollStats, isAdmin }) => {
  if (!userPollStats && !pollStats) return null;

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
      borderRadius: 20, padding: 25, marginBottom: 30,
      border: '1px solid rgba(245, 158, 11, 0.2)'
    }} data-testid="poll-statistics-section">
      <h2 style={{ color: '#0F172A', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        📊 Poll Insights
        <span style={{ background: 'rgba(16, 185, 129, 0.2)', padding: '4px 12px', borderRadius: 15, fontSize: '0.7rem', fontWeight: 600, color: '#10b981' }}>COMMUNITY</span>
      </h2>

      {userPollStats && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ color: '#047857', fontSize: '1rem', marginBottom: 12 }}>Your Poll Activity</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            {[
              { label: 'Polls Created', value: userPollStats.polls_created, icon: '📝', color: '#FDE047' },
              { label: 'Active Now', value: userPollStats.active_polls, icon: '🟢', color: '#10b981' },
              { label: 'Votes Received', value: userPollStats.total_votes_received, icon: '✅', color: '#3b82f6' },
              { label: 'Polls Voted', value: userPollStats.polls_voted_on, icon: '🗳️', color: '#f59e0b' },
            ].map((stat, i) => (
              <div key={i} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, padding: '12px 15px', textAlign: 'center', border: `1px solid ${stat.color}20` }}>
                <div style={{ fontSize: '1.3rem', marginBottom: 4 }}>{stat.icon}</div>
                <div style={{ color: stat.color, fontSize: '1.4rem', fontWeight: 700 }}>{stat.value}</div>
                <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && pollStats && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 20 }}>
          <h3 style={{ color: '#047857', fontSize: '1rem', marginBottom: 12 }}>Platform Stats (Admin)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            {[
              { label: 'Total Polls', value: pollStats.total_polls, icon: '📊', color: '#047857' },
              { label: 'Total Votes', value: pollStats.total_votes, icon: '🗳️', color: '#FDE047' },
              { label: 'Avg Votes/Poll', value: pollStats.avg_votes_per_poll?.toFixed(1), icon: '📈', color: '#10b981' },
              { label: 'Unique Voters', value: pollStats.unique_voters, icon: '👥', color: '#3b82f6' },
            ].map((stat, i) => (
              <div key={i} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, padding: '12px 15px', textAlign: 'center', border: `1px solid ${stat.color}20` }}>
                <div style={{ fontSize: '1.3rem', marginBottom: 4 }}>{stat.icon}</div>
                <div style={{ color: stat.color, fontSize: '1.4rem', fontWeight: 700 }}>{stat.value}</div>
                <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const BookPromo = () => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(253, 230, 138, 0.15) 0%, rgba(253, 224, 71, 0.15) 100%)',
    borderRadius: 20, padding: 25, marginTop: 30,
    border: '1px solid rgba(253, 230, 138, 0.3)'
  }}>
    <h2 style={{ color: '#047857', marginBottom: 15, display: 'flex', alignItems: 'center', gap: 10 }}>
      📚 Speaking of Statistics...
    </h2>
    <p style={{ color: '#334155', marginBottom: 15, lineHeight: 1.6 }}>
      <strong>&quot;Letters to Evelyn&quot;</strong> by John Selman has <strong style={{ color: '#10b981' }}>19 Five-Star Reviews</strong> on Readers&apos; Favorite! 
      That&apos;s a 100% satisfaction rate - better stats than most protocols! 🚀
    </p>
    <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap' }}>
      <a href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US" target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ padding: '10px 20px' }}>
        📖 Get on Google Play Books
      </a>
    </div>
  </div>
);

// Main Component
const StatisticsPage = ({ showToast }) => {
  const { token, user } = useAuth();
  const filters = useFilters();
  const {
    educatedIndividualFilters, setEducatedIndividualFilters,
    eiMinWords, setEiMinWords,
    minWordCount, setMinWordCount,
    documentTypes, selectedDocTypes, setSelectedDocTypes,
    docTypeMinWords, setDocTypeMinWords,
    certificationFilters, setCertificationFilters,
    certMinWords, setCertMinWords,
    contentMinWords, setContentMinWords,
    minQualityScore, setMinQualityScore,
    exactMatch, setExactMatch,
    searchDepth, setSearchDepth,
    aggregation, setAggregation,
    selectedCategories, setSelectedCategories,
    showControlPanel, setShowControlPanel,
    controlPanelLayout, setControlPanelLayout,
    filterHistory, getRecommendations,
    loadPreferences, exportFilters,
    undo, redo, canUndo, canRedo
  } = filters;
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ sales: [], revenue: [] });
  const [mostCopied, setMostCopied] = useState({ leaderboard: [], stats: {} });
  const [pollStats, setPollStats] = useState(null);
  const [userPollStats, setUserPollStats] = useState(null);
  const [leaderboardTab, setLeaderboardTab] = useState('sales');
  const [loading, setLoading] = useState(true);
  const [funnyMessage] = useState(() => FUNNY_STATS_MESSAGES[Math.floor(Math.random() * FUNNY_STATS_MESSAGES.length)]);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [statsMapCollapsed, setStatsMapCollapsed] = useState(false);
  const [drillDownUrls, setDrillDownUrls] = useState(null);
  const [drillDownTitle, setDrillDownTitle] = useState('');

  // Chart drill-down: fetch URLs matching a chart segment
  const handleChartDrillDown = (dimension, value, label) => {
    if (!value) return;
    setDrillDownTitle(`${label || value} (${dimension})`);
    setDrillDownUrls([]);
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/statistics/chart-drilldown?dimension=${encodeURIComponent(dimension)}&value=${encodeURIComponent(value)}`, true);
    if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function () {
      if (x.status < 300) {
        try {
          const d = JSON.parse(x.responseText);
          setDrillDownUrls(d.urls || []);
          setDrillDownTitle(`${label || value} — ${d.total} total (showing ${d.showing})`);
        } catch (e) { setDrillDownUrls(null); }
      }
    };
    x.onerror = () => setDrillDownUrls(null);
    x.send();
  };
  const [selectedStatType, setSelectedStatType] = useState(null);
  
  // Use educatedIndividualFilters from context (aliased for backward compat)
  const hotCivilianFilters = educatedIndividualFilters;
  const setHotCivilianFilters = setEducatedIndividualFilters;
  
  // Data source toggle: 'worldwide' or 'personal'
  const [dataSource, setDataSource] = useState('worldwide');
  const [showProgrammerMode, setShowProgrammerMode] = useState(false);
  
  // Filtered stats based on active filter controls - reacts to ALL filter changes
  const filteredStats = React.useMemo(() => {
    if (!stats) return null;
    const activeDocTypes = selectedDocTypes.length > 0 && selectedDocTypes.length < documentTypes.length;
    const activeCerts = Object.values(certificationFilters).some(v => v);
    const activeEI = Object.entries(educatedIndividualFilters).filter(([k, v]) => v && k !== 'favor_visuals' && k !== 'favor_pictures').length;
    const activeCategories = selectedCategories.length > 0;
    const hasFilters = activeDocTypes || activeCerts || activeEI > 0 || minQualityScore > 0 || activeCategories;
    
    if (!hasFilters) return stats;
    
    // Clone stats and apply filter adjustments
    const adjusted = JSON.parse(JSON.stringify(stats));
    
    // If categories are selected, filter category_counts to only show those categories
    if (activeCategories && adjusted.category_counts) {
      const catNames = (adjusted.category_counts || []).map(c => c.name || c.category);
      const selectedCatNames = categories
        .filter(c => selectedCategories.includes(c.id || c._id))
        .map(c => c.name);
      
      adjusted.category_counts = adjusted.category_counts.filter(c => 
        selectedCatNames.includes(c.name || c.category)
      );
      
      // Adjust total based on selected categories' proportion
      if (catNames.length > 0) {
        const totalCatUrls = adjusted.category_counts.reduce((sum, c) => sum + (c.count || 0), 0);
        const originalTotal = adjusted.total_results || adjusted.total_urls || 1;
        if (totalCatUrls > 0 && totalCatUrls < originalTotal) {
          adjusted.total_results = totalCatUrls;
          adjusted.total_urls = totalCatUrls;
        }
      }
    }
    
    // Apply ratio multiplier for other filters
    const ratio = 1 - ((activeDocTypes ? 0.15 : 0) + (activeCerts ? 0.2 : 0) + (activeEI > 0 && activeEI < 7 ? 0.1 * (7 - activeEI) : 0) + (minQualityScore > 30 ? 0.15 : 0));
    if (ratio < 1) {
      if (adjusted.total_results) adjusted.total_results = Math.round(adjusted.total_results * Math.max(0.05, ratio));
      if (adjusted.total_urls) adjusted.total_urls = Math.round(adjusted.total_urls * Math.max(0.05, ratio));
    }
    
    adjusted._filtered = true;
    adjusted._ratio = ratio;
    adjusted._activeFilterCount = (activeDocTypes ? 1 : 0) + (activeCerts ? 1 : 0) + (activeEI > 0 ? 1 : 0) + (minQualityScore > 0 ? 1 : 0) + (activeCategories ? 1 : 0);
    return adjusted;
  }, [stats, selectedDocTypes, documentTypes.length, certificationFilters, educatedIndividualFilters, minQualityScore, selectedCategories, categories]);
  
  // Easter Egg Statistics state
  const [easterEggStats, setEasterEggStats] = useState(null);
  const [userEasterEggStats, setUserEasterEggStats] = useState(null);
  const [easterEggLeaderboard, setEasterEggLeaderboard] = useState([]);
  
  // AI Search state
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchMode, setAiSearchMode] = useState('comprehensive');
  const [dbSearchLoading, setDbSearchLoading] = useState(false);
  const [dbSearchMode, setDbSearchMode] = useState('smart');

  // AI Intelligent Search for Statistics
  const aiSearchFromStats = async () => {
    if (!aiSearchQuery.trim()) {
      showToast('Please enter a search query', 'error');
      return;
    }
    
    // Build enhanced query with active hot civilian topics
    const activeTopics = Object.entries(hotCivilianFilters)
      .filter(([, active]) => active)
      .map(([topic]) => topic);
    let enhancedQuery = aiSearchQuery;
    if (activeTopics.length > 0 && activeTopics.length < 7) {
      enhancedQuery = `${aiSearchQuery} (${activeTopics.join(', ')})`;
    }
    
    setAiSearchLoading(true);
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/ai-search`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() {
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        showToast(`${data.message}`, 'success');
        fetchStatistics();
      } else {
        try { showToast(JSON.parse(xhr.responseText).detail || 'AI Search failed', 'error'); } catch(e) { showToast('AI Search failed', 'error'); }
      }
      setAiSearchLoading(false);
    };
    xhr.onerror = function() { setAiSearchLoading(false); showToast('AI Search failed', 'error'); };
    xhr.send(JSON.stringify({ query: enhancedQuery, mode: aiSearchMode, auto_categorize: true }));
  };

  // Auto-Categorize Search from Statistics (XHR)
  const autoCategorizeFroStats = () => {
    if (!aiSearchQuery.trim()) {
      showToast('Please enter a search query', 'error');
      return;
    }
    
    // Build enhanced query with active hot civilian topics
    const activeTopics = Object.entries(hotCivilianFilters)
      .filter(([, active]) => active)
      .map(([topic]) => topic);
    let enhancedQuery = aiSearchQuery;
    if (activeTopics.length > 0 && activeTopics.length < 7) {
      enhancedQuery = `${aiSearchQuery} (${activeTopics.join(', ')})`;
    }
    
    setAiSearchLoading(true);
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/auto-categorize`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() {
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        if (data.task_id) {
          showToast('Auto-categorize started in background...', 'info');
          const startTime = Date.now();
          const MAX_POLL_TIME = 300000; // 5 minute safety timeout
          const pollInterval = setInterval(() => {
            // Safety timeout: stop polling after 5 minutes
            if (Date.now() - startTime > MAX_POLL_TIME) {
              clearInterval(pollInterval);
              showToast('Auto-categorize is taking too long. Check task history for status.', 'warning');
              setAiSearchLoading(false);
              return;
            }
            const pollXhr = new XMLHttpRequest();
            pollXhr.open('GET', `${API}/task-status/${data.task_id}`, true);
            pollXhr.setRequestHeader('Authorization', `Bearer ${token}`);
            pollXhr.onload = function() {
              if (pollXhr.status === 200) {
                const task = JSON.parse(pollXhr.responseText);
                if (task.status === 'completed') {
                  clearInterval(pollInterval);
                  showToast(`${task.result?.message || 'Complete!'}`, 'success');
                  fetchStatistics();
                  setAiSearchLoading(false);
                } else if (task.status === 'failed') {
                  clearInterval(pollInterval);
                  showToast(`Auto-categorize failed: ${task.error || 'Unknown error'}`, 'error');
                  setAiSearchLoading(false);
                }
              }
            };
            pollXhr.onerror = function() {
              clearInterval(pollInterval);
              setAiSearchLoading(false);
            };
            pollXhr.send();
          }, 3000);
        } else {
          showToast(`${data.message}`, 'success');
          fetchStatistics();
          setAiSearchLoading(false);
        }
      } else {
        try { showToast(JSON.parse(xhr.responseText).detail || 'Auto-categorization failed', 'error'); } catch(e) { showToast('Auto-categorization failed', 'error'); }
        setAiSearchLoading(false);
      }
    };
    xhr.onerror = function() { setAiSearchLoading(false); showToast('Auto-categorization failed', 'error'); };
    xhr.send(JSON.stringify({ query: enhancedQuery }));
  };

  // Database Text Search from Statistics (XHR)
  const databaseSearchFromStats = () => {
    if (!aiSearchQuery.trim()) {
      showToast('Please enter a search query', 'error');
      return;
    }
    
    // Build enhanced query with active hot civilian topics
    const activeTopics = Object.entries(hotCivilianFilters)
      .filter(([, active]) => active)
      .map(([topic]) => topic);
    let enhancedQuery = aiSearchQuery;
    if (activeTopics.length > 0 && activeTopics.length < 7) {
      enhancedQuery = `${aiSearchQuery} (${activeTopics.join(', ')})`;
    }
    
    setDbSearchLoading(true);
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/database-search`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() {
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        showToast(`${data.message}`, 'success');
      } else {
        try { showToast(JSON.parse(xhr.responseText).detail || 'Database search failed', 'error'); } catch(e) { showToast('Database search failed', 'error'); }
      }
      setDbSearchLoading(false);
    };
    xhr.onerror = function() { setDbSearchLoading(false); showToast('Database search failed', 'error'); };
    xhr.send(JSON.stringify({ query: enhancedQuery, mode: dbSearchMode, limit: 100 }));
  };

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    fetchCategories();
    
    let done = 0;
    const total = 3;
    const onDone = () => {
      done++;
      if (done >= total) {
        setLoading(false);
        fetchPollStatistics();
        fetchEasterEggStatistics();
      }
    };
    
    // Safety timeout - force complete loading after 8 seconds
    const safetyTimer = setTimeout(() => {
      if (done < total) {
        setLoading(false);
        fetchPollStatistics();
        fetchEasterEggStatistics();
      }
    }, 8000);
    
    const makeXHR = (url, callback) => {
      const x = new XMLHttpRequest();
      x.open('GET', url, true);
      x.setRequestHeader('Authorization', `Bearer ${token}`);
      x.onload = function() {
        if (x.status === 200) {
          try { callback(JSON.parse(x.responseText)); } catch (e) { callback(null); }
        } else { callback(null); }
      };
      x.onerror = function() { callback(null); };
      x.ontimeout = function() { callback(null); };
      x.timeout = 7000;
      x.send();
    };
    
    makeXHR(`${API}/statistics?source=${dataSource}`, (data) => {
      if (data) setStats(data);
      onDone();
    });
    makeXHR(`${API}/marketplace/leaderboard/sales`, (data) => {
      setLeaderboard(data || { sales: [], revenue: [] });
      onDone();
    });
    makeXHR(`${API}/statistics/most-copied`, (data) => {
      setMostCopied(data || { leaderboard: [], stats: {} });
      onDone();
    });
    
    return () => clearTimeout(safetyTimer);
  }, [dataSource, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPollStatistics = () => {
    if (!token) return;
    const xhrGet = (url, callback) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.onload = function() {
        if (xhr.status === 200) {
          try { callback(JSON.parse(xhr.responseText)); } catch (e) { callback(null); }
        }
      };
      xhr.onerror = function() {};
      xhr.send();
    };
    xhrGet(`${API}/polls/user/statistics`, (data) => { if (data) setUserPollStats(data); });
    if (user?.is_admin) {
      xhrGet(`${API}/polls/admin/statistics`, (data) => { if (data) setPollStats(data); });
    }
  };

  const fetchEasterEggStatistics = () => {
    const xhrGet = (url, authHeader, callback) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      if (authHeader) xhr.setRequestHeader('Authorization', authHeader);
      xhr.onload = function() {
        if (xhr.status === 200) {
          try { callback(JSON.parse(xhr.responseText)); } catch (e) { callback(null); }
        }
      };
      xhr.onerror = function() {};
      xhr.send();
    };
    xhrGet(`${API}/easter-eggs/stats`, null, (data) => { if (data) setEasterEggStats(data); });
    xhrGet(`${API}/easter-eggs/leaderboard`, null, (data) => { if (data) setEasterEggLeaderboard(data?.leaderboard || []); });
    if (token) {
      xhrGet(`${API}/easter-eggs/my-discoveries`, `Bearer ${token}`, (data) => { if (data) setUserEasterEggStats(data); });
    }
  };

  const fetchMostCopied = () => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/statistics/most-copied`, true);
    xhr.onload = function() {
      if (xhr.status === 200) {
        try { setMostCopied(JSON.parse(xhr.responseText)); } catch (e) {}
      }
    };
    xhr.onerror = function() {};
    xhr.send();
  };

  const fetchStatistics = () => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/statistics/dashboard`, true);
    xhr.onload = function() {
      if (xhr.status === 200) {
        try { setStats(JSON.parse(xhr.responseText)); } catch (e) {}
      }
      setLoading(false);
    };
    xhr.onerror = function() { setLoading(false); };
    xhr.send();
  };

  const fetchCategories = () => {
    if (!token) return;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/categories`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() {
      if (xhr.status === 200) {
        try { setCategories(JSON.parse(xhr.responseText).categories || JSON.parse(xhr.responseText) || []); } catch (e) {}
      }
    };
    xhr.onerror = function() {};
    xhr.send();
  };


  const fetchLeaderboards = () => {
    const xhrGet = (url, callback) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.onload = function() {
        if (xhr.status === 200) {
          try { callback(JSON.parse(xhr.responseText)); } catch (e) { callback(null); }
        } else { callback(null); }
      };
      xhr.onerror = function() { callback(null); };
      xhr.send();
    };
    xhrGet(`${API}/marketplace/leaderboard/sales`, (salesData) => {
      xhrGet(`${API}/marketplace/leaderboard/revenue`, (revenueData) => {
        setLeaderboard({
          sales: salesData?.sales || salesData || [],
          revenue: revenueData?.revenue || revenueData || []
        });
      });
    });
  };

  const populateMapForStat = (statType, data) => {
    setSelectedStatType(statType);
    const markers = [];
    
    if (statType === 'countries') {
      (data || []).forEach((item, idx) => {
        const coords = COUNTRY_COORDINATES[item.name];
        if (coords) markers.push({ id: `country-${idx}`, position: coords, label: item.name, value: item.count || item.value || 0, color: COLORS[idx % COLORS.length], type: 'country' });
      });
    } else if (statType === 'us_states') {
      (data || []).forEach((item, idx) => {
        const coords = STATE_COORDINATES[item.name];
        if (coords) markers.push({ id: `state-${idx}`, position: coords, label: item.name, value: item.count || item.value || 0, color: COLORS[idx % COLORS.length], type: 'state' });
      });
    } else if (statType === 'top_sellers') {
      const stateNames = Object.keys(STATE_COORDINATES);
      (data || []).forEach((item, idx) => {
        const coords = STATE_COORDINATES[stateNames[idx % stateNames.length]];
        if (coords) markers.push({ id: `seller-${idx}`, position: [coords[0] + (Math.random() - 0.5) * 2, coords[1] + (Math.random() - 0.5) * 2], label: item.username || item.name, value: item.total_sales || 0, color: COLORS[idx % COLORS.length], type: 'seller' });
      });
    } else if (statType === 'most_copied') {
      const stateNames = Object.keys(STATE_COORDINATES);
      (mostCopied.leaderboard || []).forEach((item, idx) => {
        const coords = STATE_COORDINATES[stateNames[idx % stateNames.length]];
        if (coords) markers.push({ id: `copied-${idx}`, position: [coords[0] + (Math.random() - 0.5) * 2, coords[1] + (Math.random() - 0.5) * 2], label: item.name, value: item.copy_count || 0, color: COLORS[idx % COLORS.length], type: 'protocol' });
      });
    }
    
    setMapMarkers(markers);
  };

  const clearMap = () => { setMapMarkers([]); setSelectedStatType(null); };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', color: '#0F172A' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: 20 }}>📊</div>
          <p>Crunching numbers faster than a squirrel hoards acorns...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }} data-testid="statistics-page">
      {/* Ultra-Dense Control Panel (shared via FilterContext) */}
      <UltraDenseControlPanel
        show={showControlPanel} onClose={() => setShowControlPanel(false)}
        layout={controlPanelLayout} setLayout={setControlPanelLayout}
        educatedIndividualFilters={educatedIndividualFilters} setEducatedIndividualFilters={setEducatedIndividualFilters}
        eiMinWords={eiMinWords} setEiMinWords={setEiMinWords}
        selectedDocTypes={selectedDocTypes} setSelectedDocTypes={setSelectedDocTypes} documentTypes={documentTypes}
        docTypeMinWords={docTypeMinWords} setDocTypeMinWords={setDocTypeMinWords}
        certificationFilters={certificationFilters} setCertificationFilters={setCertificationFilters}
        certMinWords={certMinWords} setCertMinWords={setCertMinWords}
        minQualityScore={minQualityScore} setMinQualityScore={setMinQualityScore}
        minWordCount={minWordCount} setMinWordCount={setMinWordCount}
        exactMatch={exactMatch} setExactMatch={setExactMatch}
        searchDepth={searchDepth} setSearchDepth={setSearchDepth}
        aggregation={aggregation} setAggregation={setAggregation}
        selectedCategories={selectedCategories} setSelectedCategories={setSelectedCategories} categories={categories}
        contentMinWords={contentMinWords} setContentMinWords={setContentMinWords}
        filterHistory={filterHistory} getRecommendations={getRecommendations}
        showToast={showToast}
      />
      <PowerUserControlPanel show={showProgrammerMode} onClose={() => setShowProgrammerMode(false)} />
      {/* Control Panel Toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10, gap: 8 }}>
        <button onClick={undo} disabled={!canUndo} data-testid="stats-undo-btn" title="Undo"
          style={{ background: canUndo ? 'rgba(245,158,11,0.12)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${canUndo ? 'rgba(245,158,11,0.3)' : 'rgba(203, 213, 225, 0.8)'}`, color: canUndo ? '#f59e0b' : '#555', padding: '7px 10px', borderRadius: 8, cursor: canUndo ? 'pointer' : 'default', fontSize: '0.74rem' }}>
          <i className="fas fa-undo" />
        </button>
        <button onClick={redo} disabled={!canRedo} data-testid="stats-redo-btn" title="Redo"
          style={{ background: canRedo ? 'rgba(16,185,129,0.12)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${canRedo ? 'rgba(16,185,129,0.3)' : 'rgba(203, 213, 225, 0.8)'}`, color: canRedo ? '#10b981' : '#555', padding: '7px 10px', borderRadius: 8, cursor: canRedo ? 'pointer' : 'default', fontSize: '0.74rem' }}>
          <i className="fas fa-redo" />
        </button>
        <button onClick={() => setShowControlPanel(true)} data-testid="stats-open-control-panel-btn"
          className="btn-ocean ocean-shimmer"
          style={{ background: 'linear-gradient(135deg, rgba(253, 230, 138,0.12), rgba(252, 211, 77,0.08))', border: '1px solid rgba(253, 230, 138,0.3)', color: '#38bdf8', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace', letterSpacing: 0.8, position: 'relative', overflow: 'hidden' }}>
          [CTRL]
        </button>
        <button onClick={() => setShowProgrammerMode(true)} data-testid="stats-open-programmer-mode-btn"
          className="btn-ocean"
          style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(253, 230, 138,0.08))', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700, fontFamily: '"SF Mono", monospace', letterSpacing: 0.8 }}>
          [PRO]
        </button>
      </div>
      <HeroBanner funnyMessage={funnyMessage} funnyFact={stats?.funny_fact} />
      
      {/* Data Source Toggle */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        marginBottom: 25
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: '#334155', 
          borderRadius: 25, 
          padding: '4px',
          border: '1px solid rgba(252, 211, 77, 0.3)'
        }} data-testid="stats-data-source-toggle">
          <button
            onClick={() => setDataSource('worldwide')}
            style={{
              padding: '10px 24px',
              borderRadius: 22,
              border: 'none',
              background: dataSource === 'worldwide' 
                ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                : 'transparent',
              color: dataSource === 'worldwide' ? '#fff' : '#a1a1aa',
              fontWeight: dataSource === 'worldwide' ? 600 : 400,
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
            data-testid="stats-data-source-worldwide"
          >
            🌍 Worldwide Database
          </button>
          <button
            onClick={() => setDataSource('personal')}
            style={{
              padding: '10px 24px',
              borderRadius: 22,
              border: 'none',
              background: dataSource === 'personal' 
                ? 'linear-gradient(135deg, #10b981, #059669)' 
                : 'transparent',
              color: dataSource === 'personal' ? '#fff' : '#a1a1aa',
              fontWeight: dataSource === 'personal' ? 600 : 400,
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
            data-testid="stats-data-source-personal"
          >
            👤 My Data Only
          </button>
        </div>
      </div>

      {/* Date Range & Export Controls */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }} data-testid="stats-export-controls">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#475569', padding: '6px 12px', borderRadius: 10, border: '1px solid rgba(253, 224, 71,0.2)' }}>
          <span style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>From:</span>
          <input type="date" data-testid="stats-date-from"
            style={{ background: 'rgba(255, 255, 255,0.8)', border: '1px solid rgba(253, 224, 71,0.3)', color: '#334155', padding: '4px 8px', borderRadius: 6, fontSize: '0.78rem' }} />
          <span style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>To:</span>
          <input type="date" data-testid="stats-date-to"
            style={{ background: 'rgba(255, 255, 255,0.8)', border: '1px solid rgba(253, 224, 71,0.3)', color: '#334155', padding: '4px 8px', borderRadius: 6, fontSize: '0.78rem' }} />
        </div>
        <button onClick={() => {
          const x = new XMLHttpRequest();
          x.open('GET', `${API}/statistics/export?source=${dataSource}`, true);
          x.setRequestHeader('Authorization', `Bearer ${token}`);
          x.responseType = 'blob';
          x.onload = function() {
            if (x.status === 200) {
              const blob = new Blob([x.response], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'infopilot_statistics.csv';
              a.click();
              window.URL.revokeObjectURL(url);
              showToast('CSV exported!', 'success');
            } else {
              showToast('Export failed', 'error');
            }
          };
          x.onerror = function() { showToast('Export failed', 'error'); };
          x.send();
        }} data-testid="stats-export-csv-btn"
          style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#0F172A', border: 'none', padding: '8px 16px', borderRadius: 10, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          Export CSV
        </button>
      </div>
      
      {/* AI Search Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.15), rgba(253, 230, 138, 0.15))',
        borderRadius: 16,
        padding: 20,
        marginBottom: 30,
        border: '1px solid rgba(252, 211, 77, 0.3)'
      }}>
        <h3 style={{ color: '#047857', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          🤖 AI-Powered Search
          <span style={{ fontSize: '0.7rem', background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '3px 8px', borderRadius: 6 }}>
            Search Google, Bing, DuckDuckGo, Brave & More
          </span>
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <input
            type="text"
            value={aiSearchQuery}
            onChange={(e) => setAiSearchQuery(e.target.value)}
            placeholder="Enter search query for AI-powered intelligent search..."
            style={{
              flex: 1,
              minWidth: 250,
              padding: '12px 15px',
              background: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(252, 211, 77, 0.4)',
              borderRadius: 10,
              color: '#0F172A',
              fontSize: '1rem'
            }}
            onKeyPress={(e) => e.key === 'Enter' && aiSearchFromStats()}
            data-testid="stats-ai-search-input"
          />
          <button
            onClick={autoCategorizeFroStats}
            disabled={aiSearchLoading || !aiSearchQuery.trim()}
            className="btn"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#0F172A',
              padding: '12px 20px',
              fontWeight: 600,
              opacity: (!aiSearchQuery.trim() || aiSearchLoading) ? 0.5 : 1
            }}
            data-testid="stats-auto-categorize-btn"
          >
            {aiSearchLoading ? '⏳' : '🎯'} Search & Auto-Categorize
          </button>
          <button
            onClick={aiSearchFromStats}
            disabled={aiSearchLoading || !aiSearchQuery.trim()}
            className="btn"
            style={{
              background: 'linear-gradient(135deg, #FDE047, #FCD34D)',
              color: '#0F172A',
              padding: '12px 20px',
              fontWeight: 600,
              opacity: (!aiSearchQuery.trim() || aiSearchLoading) ? 0.5 : 1
            }}
            data-testid="stats-ai-search-btn"
          >
            {aiSearchLoading ? '🤖 Searching...' : '🤖 AI Intelligent Search'}
          </button>
          <select
            value={aiSearchMode}
            onChange={(e) => setAiSearchMode(e.target.value)}
            style={{
              background: 'rgba(252, 211, 77, 0.3)',
              border: '1px solid rgba(252, 211, 77, 0.4)',
              color: '#047857',
              padding: '10px 15px',
              borderRadius: 8,
              fontSize: '0.9rem'
            }}
          >
            <option value="comprehensive">📊 Comprehensive</option>
            <option value="news">📰 News Focus</option>
            <option value="research">🔬 Research Focus</option>
          </select>
        </div>
        {/* Database Search Row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={databaseSearchFromStats}
            disabled={dbSearchLoading || !aiSearchQuery.trim()}
            className="btn"
            style={{
              background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
              color: '#0F172A',
              padding: '12px 20px',
              fontWeight: 600,
              opacity: (!aiSearchQuery.trim() || dbSearchLoading) ? 0.5 : 1
            }}
            data-testid="stats-database-search-btn"
          >
            {dbSearchLoading ? '📚 Searching...' : '📚 Database Text Search'}
          </button>
          <select
            value={dbSearchMode}
            onChange={(e) => setDbSearchMode(e.target.value)}
            style={{
              background: 'rgba(6, 182, 212, 0.3)',
              border: '1px solid rgba(6, 182, 212, 0.4)',
              color: '#22d3ee',
              padding: '10px 15px',
              borderRadius: 8,
              fontSize: '0.9rem'
            }}
          >
            <option value="smart">🧠 Smart Match</option>
            <option value="exact">🎯 Exact Phrase</option>
            <option value="fuzzy">🔍 Fuzzy Match</option>
          </select>
        </div>
        <p style={{ color: '#a1a1aa', fontSize: '0.8rem', marginTop: 10, marginBottom: 0 }}>
          💡 <strong>AI Search:</strong> Searches Google, Bing, DuckDuckGo, Brave with GPT keyword expansion. <strong>Database Search:</strong> Finds content in your already categorized results.
          {Object.values(hotCivilianFilters).some(v => v) && Object.values(hotCivilianFilters).some(v => !v) && (
            <span style={{ color: '#f59e0b', marginLeft: 8 }}>
              <strong>Active topic filters:</strong> {Object.entries(hotCivilianFilters).filter(([, v]) => v).map(([k]) => k).join(', ')}
            </span>
          )}
        </p>
      </div>
      
      <QuickStatsGrid stats={stats} />

      {/* Hot Civilian Filter - Educational Content from Experts */}
      <div style={{
        marginBottom: 25,
        padding: 20,
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(239, 68, 68, 0.1))',
        borderRadius: 16,
        border: '1px solid rgba(245, 158, 11, 0.3)'
      }} data-testid="hot-civilian-filter-stats">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ color: '#f59e0b', margin: 0, fontSize: '1rem' }}>
            Hot Civilian Topics
            <span style={{ fontSize: '0.75rem', color: '#a1a1aa', fontWeight: 'normal', marginLeft: 8 }}>
              Filter statistics by educational content from self-taught experts
            </span>
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setHotCivilianFilters({
                science: true, engineering: true, construction: true,
                culinary: true, trades: true, mathematics: true, technology: true
              })}
              style={{
                background: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                color: '#10b981',
                padding: '5px 12px',
                borderRadius: 6,
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
              data-testid="hot-civilian-all-stats"
            >
              All Topics
            </button>
            <button
              onClick={() => setHotCivilianFilters({
                science: false, engineering: false, construction: false,
                culinary: false, trades: false, mathematics: false, technology: false
              })}
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                padding: '5px 12px',
                borderRadius: 6,
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
              data-testid="hot-civilian-clear-stats"
            >
              Clear
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            { id: 'science', label: 'Science', desc: 'Chemistry, Physics, Biology' },
            { id: 'engineering', label: 'Engineering', desc: 'Electrical, Mechanical, Civil' },
            { id: 'construction', label: 'Construction', desc: 'Framing, Plumbing, HVAC' },
            { id: 'culinary', label: 'Culinary', desc: 'Cooking, Recipes, Methods' },
            { id: 'trades', label: 'Trades', desc: 'Welding, Carpentry, Masonry' },
            { id: 'mathematics', label: 'Mathematics', desc: 'Calculus, Algebra, Geometry' },
            { id: 'technology', label: 'Technology', desc: 'Programming, Hardware, IT' }
          ].map(topic => (
            <label
              key={topic.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: hotCivilianFilters[topic.id]
                  ? 'rgba(245, 158, 11, 0.2)'
                  : '#475569',
                border: hotCivilianFilters[topic.id]
                  ? '2px solid #f59e0b'
                  : '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              title={topic.desc}
              data-testid={`hot-civilian-${topic.id}-stats`}
            >
              <input
                type="checkbox"
                checked={hotCivilianFilters[topic.id]}
                onChange={() => setHotCivilianFilters(prev => ({
                  ...prev,
                  [topic.id]: !prev[topic.id]
                }))}
                style={{ accentColor: '#f59e0b' }}
              />
              <span style={{ 
                color: hotCivilianFilters[topic.id] ? '#f59e0b' : '#e2e8f0',
                fontSize: '0.8rem',
                fontWeight: hotCivilianFilters[topic.id] ? 600 : 400
              }}>
                {topic.label}
              </span>
            </label>
          ))}
        </div>
        <p style={{ color: '#71717a', fontSize: '0.75rem', marginTop: 10, marginBottom: 0 }}>
          Find expert knowledge from self-educated individuals sharing academic and trade expertise.
        </p>
      </div>

      {/* XP & Easter Egg Analytics Suite - 8+ Advanced Charts */}
      <XPEasterEggAnalyticsSuite 
        userStats={userEasterEggStats}
        globalStats={easterEggStats}
        leaderboard={easterEggLeaderboard}
        token={token}
      />

      {/* Charts Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 25, marginBottom: 30 }}>
        {/* Countries Pie Chart */}
        <div style={{ background: '#334155', borderRadius: 16, padding: 25, border: '1px solid rgba(253, 224, 71, 0.2)', transition: 'border-color 0.3s' }} data-testid="chart-countries">
          <h3 style={{ color: '#0F172A', marginBottom: 20 }}>Results by Country</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie 
                data={stats?.countries?.countries || []} 
                dataKey="count" 
                nameKey="name" 
                cx="50%" cy="50%" 
                outerRadius={100} 
                label={({ name, percentage }) => `${name}: ${percentage}%`}
                animationBegin={0}
                animationDuration={800}
                onClick={(data) => {
                  if (data?.name) {
                    handleChartDrillDown('country', data.name, `${data.name}: ${data.count} results`);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {(stats?.countries?.countries || []).map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} stroke="rgba(0,0,0,0.3)" strokeWidth={1} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ background: 'rgba(253, 251, 247, 0.95)', border: '1px solid rgba(253, 224, 71, 0.4)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
                itemStyle={{ color: '#334155' }}
                formatter={(value, name) => [`${value} results`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
          <p style={{ color: '#71717a', fontSize: '0.7rem', textAlign: 'center', marginTop: 8 }}>Click a segment to drill into URLs</p>
        </div>

        {/* US States Bar Chart */}
        <div style={{ background: '#334155', borderRadius: 16, padding: 25, border: '1px solid rgba(253, 224, 71, 0.2)' }} data-testid="chart-us-states">
          <h3 style={{ color: '#0F172A', marginBottom: 20 }}>US States Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.us_states?.states || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="code" stroke="#888" tick={{ fontSize: 11 }} />
              <YAxis stroke="#888" />
              <Tooltip 
                contentStyle={{ background: 'rgba(253, 251, 247, 0.95)', border: '1px solid rgba(253, 224, 71, 0.4)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
                labelStyle={{ color: '#047857', fontWeight: 600 }}
                formatter={(value) => [`${value} results`, 'Count']}
                cursor={{ fill: 'rgba(253, 224, 71, 0.1)' }}
              />
              <Bar 
                dataKey="count" 
                fill="#FDE047" 
                radius={[4, 4, 0, 0]} 
                animationDuration={800}
                onClick={(data) => {
                  if (data?.name) handleChartDrillDown('us_state', data.name || data.code, `${data.name}: ${data.count} results`);
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Document Types */}
        <div style={{ background: '#334155', borderRadius: 16, padding: 25, border: '1px solid rgba(253, 224, 71, 0.2)' }} data-testid="chart-doc-types">
          <h3 style={{ color: '#0F172A', marginBottom: 20 }}>Document Types</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie 
                data={stats?.document_types?.document_types || []} 
                dataKey="count" 
                nameKey="label" 
                cx="50%" cy="50%" 
                innerRadius={50} 
                outerRadius={100} 
                paddingAngle={5} 
                label={({ label, count }) => `${label} (${count})`}
                animationBegin={200}
                animationDuration={800}
                onClick={(data) => {
                  if (data?.label) handleChartDrillDown('article_type', data.label, `${data.label}: ${data.count} documents`);
                }}
                style={{ cursor: 'pointer' }}
              >
                {(stats?.document_types?.document_types || []).map((entry, index) => (
                  <Cell key={index} fill={entry.color || COLORS[index % COLORS.length]} stroke="rgba(0,0,0,0.3)" strokeWidth={1} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ background: 'rgba(253, 251, 247, 0.95)', border: '1px solid rgba(253, 224, 71, 0.4)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
                itemStyle={{ color: '#334155' }}
                formatter={(value, name) => [`${value} documents`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
          <p style={{ color: '#71717a', fontSize: '0.7rem', textAlign: 'center', marginTop: 8 }}>Click a segment for details</p>
        </div>

        {/* Top Words */}
        <div style={{ background: '#334155', borderRadius: 16, padding: 25, border: '1px solid rgba(253, 224, 71, 0.2)' }} data-testid="chart-top-words">
          <h3 style={{ color: '#0F172A', marginBottom: 20 }}>Top 10 Protocol Words</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.top_words?.top_words || []} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis type="number" stroke="#888" />
              <YAxis dataKey="word" type="category" stroke="#888" width={60} tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ background: 'rgba(253, 251, 247, 0.95)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
                labelStyle={{ color: '#10b981', fontWeight: 600 }}
                formatter={(value) => [`${value} occurrences`, 'Count']}
                cursor={{ fill: 'rgba(16, 185, 129, 0.1)' }}
              />
              <Bar 
                dataKey="count" 
                fill="#10b981" 
                radius={[0, 4, 4, 0]} 
                animationDuration={800}
                onClick={(data) => {
                  if (data?.word) handleChartDrillDown('word', data.word, `"${data.word}": ${data.count} occurrences`);
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Drill-Down URL Window - appears when chart segment is clicked */}
      {drillDownUrls !== null && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255,0.95), rgba(253, 251, 247,0.98))',
          borderRadius: 16, padding: 25, marginBottom: 30,
          border: '2px solid rgba(253, 230, 138,0.35)',
          position: 'relative', maxHeight: 500, overflowY: 'auto'
        }} data-testid="chart-drilldown-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <h3 style={{ color: '#38bdf8', margin: 0, fontSize: '1.1rem' }}>
              <i className="fas fa-search-plus" style={{ marginRight: 8 }} />
              Chart Drill-Down: {drillDownTitle}
            </h3>
            <button onClick={() => setDrillDownUrls(null)} data-testid="close-drilldown-btn"
              style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
              <i className="fas fa-times" style={{ marginRight: 4 }} /> Close
            </button>
          </div>
          {drillDownUrls.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: '#71717a' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', marginBottom: 10, display: 'block' }} />
              Loading URLs...
            </div>
          )}
          {drillDownUrls.map((u, i) => (
            <div key={i} style={{
              padding: '10px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)',
              display: 'flex', gap: 12, alignItems: 'flex-start',
              transition: 'background 0.2s', cursor: 'pointer'
            }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(253, 230, 138,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => window.open(u.url, '_blank', 'noopener,noreferrer')}
              data-testid={`drilldown-url-${i}`}
            >
              <div style={{ flex: 1 }}>
                <a href={u.url} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem', display: 'block', marginBottom: 3 }}
                  onClick={(e) => e.stopPropagation()}>
                  {u.title || u.url}
                </a>
                {u.snippet && (
                  <p style={{ color: '#a1a1aa', fontSize: '0.72rem', margin: '2px 0 4px', lineHeight: 1.4 }}>
                    {u.snippet.substring(0, 200)}{u.snippet.length > 200 ? '...' : ''}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {u.root_domain && <span style={{ color: '#71717a', fontSize: '0.65rem' }}>{u.root_domain}</span>}
                  {u.article_type && (
                    <span style={{ color: '#047857', fontSize: '0.6rem', background: 'rgba(252, 211, 77,0.1)', padding: '1px 6px', borderRadius: 4 }}>
                      {u.article_type}
                    </span>
                  )}
                  {u.country && (
                    <span style={{ color: '#10b981', fontSize: '0.6rem', background: 'rgba(16,185,129,0.1)', padding: '1px 6px', borderRadius: 4 }}>
                      {u.country}
                    </span>
                  )}
                  {u.word_count > 0 && (
                    <span style={{ color: '#f59e0b', fontSize: '0.6rem', background: 'rgba(245,158,11,0.1)', padding: '1px 6px', borderRadius: 4 }}>
                      {u.word_count.toLocaleString()} words
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          <p style={{ color: '#71717a', fontSize: '0.65rem', textAlign: 'center', marginTop: 12 }}>
            Click any URL to open in a new tab for investigation
          </p>
        </div>
      )}

      {/* Interactive Map - Collapsible */}
      <div style={{ marginBottom: 30 }}>
        <button onClick={() => setStatsMapCollapsed(v => !v)} data-testid="stats-toggle-map-btn"
          style={{ background: statsMapCollapsed ? 'rgba(244,114,182,0.15)' : 'rgba(255, 255, 255, 0.85)', border: `1px solid ${statsMapCollapsed ? 'rgba(244,114,182,0.3)' : 'rgba(255,255,255,0.1)'}`, color: statsMapCollapsed ? '#FEF08A' : '#a1a1aa', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, marginBottom: 10 }}>
          <i className={`fas fa-${statsMapCollapsed ? 'chevron-down' : 'chevron-up'}`} style={{ marginRight: 6 }} />
          <i className="fas fa-map-marked-alt" style={{ marginRight: 4 }} />
          {statsMapCollapsed ? 'Expand Statistics Map' : 'Collapse Map'}
        </button>
        {!statsMapCollapsed && (
          <InteractiveStatsMap 
            mapMarkers={mapMarkers}
            selectedStatType={selectedStatType}
            onClearMap={clearMap}
            onSelectStat={populateMapForStat}
            stats={stats}
            leaderboard={leaderboard}
            mostCopied={mostCopied}
          />
        )}
        {statsMapCollapsed && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#71717a', fontSize: '0.8rem', background: 'rgba(255, 255, 255,0.3)', borderRadius: 12, border: '1px dashed rgba(244,114,182,0.2)' }}>
            <i className="fas fa-map" style={{ fontSize: '1.5rem', marginBottom: 6, display: 'block', color: '#047857', opacity: 0.4 }} />
            Map collapsed. Click above to expand.
          </div>
        )}

        {/* Categories & Sub-Categories below Map */}
        <CategoryTreePanel
          token={token}
          categories={[]}
          selectedCategories={selectedCategories}
          onCategoryClick={(catId) => {
            setSelectedCategories(prev =>
              prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
            );
          }}
        />
      </div>

      {/* Poll Statistics */}
      <PollStatsSection userPollStats={userPollStats} pollStats={pollStats} isAdmin={user?.is_admin} />

      {/* Easter Egg Statistics */}
      {(easterEggStats || userEasterEggStats) && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(253, 230, 138, 0.15) 50%, rgba(252, 211, 77, 0.15) 100%)',
          borderRadius: 24, padding: 30, marginBottom: 30,
          border: '2px solid rgba(251, 191, 36, 0.35)',
          boxShadow: '0 8px 32px rgba(251, 191, 36, 0.08), inset 0 0 80px rgba(251, 191, 36, 0.02)'
        }} data-testid="easter-egg-statistics-section">
          <h2 style={{ color: '#0F172A', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, fontSize: '1.6rem' }}>
            <span style={{ fontSize: '2rem', filter: 'drop-shadow(0 0 10px rgba(251,191,36,0.5))' }}>🥚</span>
            Easter Egg & Laughter Points Hub
            <span style={{ 
              background: 'linear-gradient(135deg, #047857, #0F172A)', 
              padding: '5px 16px', 
              borderRadius: 20, 
              fontSize: '0.75rem', 
              fontWeight: 700, 
              color: '#0F172A',
              boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
              letterSpacing: '0.05em'
            }}>
              LAUGHTER ZONE
            </span>
          </h2>
          
          {/* User's Personal Stats */}
          {userEasterEggStats && (
            <div style={{ marginBottom: 25 }}>
              <h3 style={{ color: '#047857', fontSize: '1.1rem', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>🎯</span> Your Egg Hunting & Laughter Stats
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
                <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 16, padding: '16px 18px', textAlign: 'center', border: '1px solid rgba(16, 185, 129, 0.3)', boxShadow: '0 2px 12px rgba(16,185,129,0.08)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>🥚</div>
                  <div style={{ color: '#10b981', fontSize: '1.8rem', fontWeight: 800, textShadow: '0 0 12px rgba(16,185,129,0.3)' }}>{userEasterEggStats.discoveries?.length || 0}</div>
                  <div style={{ color: '#d4d4d8', fontSize: '0.8rem', fontWeight: 600 }}>Eggs Caught</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 16, padding: '16px 18px', textAlign: 'center', border: '1px solid rgba(251, 191, 36, 0.3)', boxShadow: '0 2px 12px rgba(251,191,36,0.08)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>⭐</div>
                  <div style={{ color: '#047857', fontSize: '1.8rem', fontWeight: 800, textShadow: '0 0 12px rgba(251,191,36,0.3)' }}>{userEasterEggStats.total_xp || 0}</div>
                  <div style={{ color: '#d4d4d8', fontSize: '0.8rem', fontWeight: 600 }}>Laughter XP</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 16, padding: '16px 18px', textAlign: 'center', border: '1px solid rgba(253, 230, 138, 0.3)', boxShadow: '0 2px 12px rgba(253, 230, 138,0.08)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>😂</div>
                  <div style={{ color: '#047857', fontSize: '1.8rem', fontWeight: 800, textShadow: '0 0 12px rgba(253, 230, 138,0.3)' }}>
                    +{Math.floor((userEasterEggStats.total_xp || 0) * 0.25)}
                  </div>
                  <div style={{ color: '#d4d4d8', fontSize: '0.8rem', fontWeight: 600 }}>Bonus Laughter Pts</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 16, padding: '16px 18px', textAlign: 'center', border: '1px solid rgba(252, 211, 77, 0.3)', boxShadow: '0 2px 12px rgba(252, 211, 77,0.08)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>🏆</div>
                  <div style={{ color: '#047857', fontSize: '1.4rem', fontWeight: 800 }}>
                    {userEasterEggStats.discoveries?.length > 10 ? 'Pro Hunter' : 
                     userEasterEggStats.discoveries?.length > 5 ? 'Egg Collector' : 
                     userEasterEggStats.discoveries?.length > 0 ? 'Beginner' : 'New Hunter'}
                  </div>
                  <div style={{ color: '#d4d4d8', fontSize: '0.8rem', fontWeight: 600 }}>Hunter Rank</div>
                </div>
              </div>
              
              {/* Types of Eggs Caught */}
              {userEasterEggStats.discoveries?.length > 0 && (
                <div style={{ marginTop: 15 }}>
                  <h4 style={{ color: '#047857', fontSize: '0.9rem', marginBottom: 10 }}>Reward Types Collected:</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {['protocol', 'joke', 'pricing', 'motivation', 'fact', 'secret'].map(type => {
                      const count = userEasterEggStats.discoveries?.filter(d => d.reward_type === type).length || 0;
                      const typeEmojis = { protocol: '📋', joke: '😂', pricing: '💰', motivation: '💪', fact: '🧠', secret: '🤫' };
                      const typeLabels = { protocol: 'Protocols', joke: 'Jokes', pricing: 'Pricing Tips', motivation: 'Motivation', fact: 'Fun Facts', secret: 'Secrets' };
                      return (
                        <div key={type} style={{
                          background: count > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(0,0,0,0.2)',
                          border: count > 0 ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255,255,255,0.1)',
                          padding: '6px 12px',
                          borderRadius: 20,
                          fontSize: '0.8rem',
                          color: count > 0 ? '#10b981' : '#71717a'
                        }}>
                          {typeEmojis[type]} {typeLabels[type]}: {count}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Global Statistics */}
          {easterEggStats && (
            <div style={{ borderTop: userEasterEggStats ? '1px solid rgba(255,255,255,0.1)' : 'none', paddingTop: userEasterEggStats ? 20 : 0 }}>
              <h3 style={{ color: '#047857', fontSize: '1rem', marginBottom: 12 }}>🌍 Global Easter Egg Statistics</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, padding: '12px 15px', textAlign: 'center', border: '1px solid rgba(252, 211, 77, 0.2)' }}>
                  <div style={{ fontSize: '1.3rem', marginBottom: 4 }}>📊</div>
                  <div style={{ color: '#FDE047', fontSize: '1.4rem', fontWeight: 700 }}>{easterEggStats.total_discoveries || 0}</div>
                  <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Total Discoveries</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, padding: '12px 15px', textAlign: 'center', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                  <div style={{ fontSize: '1.3rem', marginBottom: 4 }}>🎁</div>
                  <div style={{ color: '#3b82f6', fontSize: '1.4rem', fontWeight: 700 }}>{easterEggStats.total_eggs_available || 0}</div>
                  <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Available Eggs</div>
                </div>
              </div>
              
              {/* Most Discovered & Rarest Eggs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginTop: 20 }}>
                {/* Most Discovered */}
                {easterEggStats.most_discovered?.length > 0 && (
                  <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, padding: 15, border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                    <h4 style={{ color: '#10b981', fontSize: '0.85rem', marginBottom: 10 }}>🔥 Most Discovered Eggs</h4>
                    {easterEggStats.most_discovered.slice(0, 3).map((egg, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: idx < 2 ? '1px solid rgba(255, 255, 255, 0.85)' : 'none' }}>
                        <span style={{ color: '#d1d5db', fontSize: '0.85rem' }}>{egg.name}</span>
                        <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.9rem' }}>{egg.discovery_count}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Rarest Eggs */}
                {easterEggStats.rarest_eggs?.length > 0 && (
                  <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, padding: 15, border: '1px solid rgba(253, 230, 138, 0.2)' }}>
                    <h4 style={{ color: '#047857', fontSize: '0.85rem', marginBottom: 10 }}>💎 Rarest Eggs</h4>
                    {easterEggStats.rarest_eggs.slice(0, 3).map((egg, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: idx < 2 ? '1px solid rgba(255, 255, 255, 0.85)' : 'none' }}>
                        <span style={{ color: '#d1d5db', fontSize: '0.85rem' }}>{egg.name}</span>
                        <span style={{ color: '#047857', fontWeight: 600, fontSize: '0.9rem' }}>{egg.discovery_count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Egg Hunter Leaderboard */}
          {easterEggLeaderboard.length > 0 && (
            <div style={{ marginTop: 20, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 20 }}>
              <h3 style={{ color: '#f59e0b', fontSize: '1rem', marginBottom: 12 }}>🏅 Top Egg Hunters</h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {easterEggLeaderboard.slice(0, 5).map((hunter, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: idx === 0 ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(253, 230, 138, 0.1))' : 'rgba(0,0,0,0.2)',
                    padding: '10px 15px',
                    borderRadius: 10,
                    border: `1px solid ${idx === 0 ? 'rgba(245, 158, 11, 0.3)' : 'rgba(255, 255, 255, 0.85)'}`
                  }}>
                    <span style={{ 
                      fontSize: '1.2rem', 
                      width: 30, 
                      textAlign: 'center' 
                    }}>
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                    </span>
                    <span style={{ flex: 1, color: '#334155', fontWeight: idx < 3 ? 600 : 400 }}>{hunter.username}</span>
                    <span style={{ color: '#10b981', fontWeight: 700 }}>{hunter.egg_count} eggs</span>
                    <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: '0.85rem' }}>{hunter.total_xp} XP</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Fun Tip */}
          <div style={{ 
            marginTop: 20, 
            background: 'rgba(252, 211, 77, 0.1)', 
            borderRadius: 10, 
            padding: 12, 
            border: '1px dashed rgba(252, 211, 77, 0.3)' 
          }}>
            <p style={{ color: '#047857', fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>
              💡 <strong>Tip:</strong> Keep your eyes open for floating Easter eggs across the app! 
              They contain jokes, protocol ideas, pricing tips, and more inspired by "Letters to Evelyn"! 🥚✨
            </p>
          </div>
        </div>
      )}

      {/* Leaderboards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 25, marginBottom: 30 }}>
        <TopSellersLeaderboard 
          leaderboard={leaderboard}
          activeTab={leaderboardTab}
          setActiveTab={setLeaderboardTab}
          onShowOnMap={populateMapForStat}
        />
        <MostCopiedLeaderboard 
          data={mostCopied}
          onShowOnMap={populateMapForStat}
        />
      </div>

      {/* Extensive Analytics Dashboard - 14+ Charts */}
      <div style={{ marginBottom: 30 }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: 20
        }}>
          <h2 style={{ 
            color: '#0F172A', 
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            📊 Extensive Analytics Suite
            <span style={{ 
              background: 'linear-gradient(135deg, #FDE047, #FDE68A)', 
              padding: '4px 12px', 
              borderRadius: 15, 
              fontSize: '0.7rem', 
              fontWeight: 600, 
              color: '#0F172A' 
            }}>
              26+ CHARTS
            </span>
          </h2>
        </div>
        <ExtensiveAnalyticsDashboard 
          data={{
            categories: stats?.categories?.categories || [],
            searchResults: [],
            userActivity: [],
            geographyData: stats?.countries || {}
          }}
          dataSource={dataSource}
          showToast={showToast}
        />
      </div>

      {/* Book Promo */}
      <MediumBookAd variant={2} />
    </div>
  );
};

export default StatisticsPage;
